<template>
  <div class="data-container">
    <b-table
      :items="tableGroup"
      :fields="tableFields"
      responsive
      show-empty
      :empty-text="emptyText"
      :busy.sync="loading"
    >
      <template scope="data" slot="actions">
        <b-button
          :variant="
            action.type === 'view' ? ' text-primary px-0 shadow-none' : 'ghost'
          "
          v-for="(action, actionIndex) of actionButtons"
          :class="[
            'border',
            {
              'mr-2': actionIndex < actionButtons.length - 1,
              'rounded-pill border-0': action.type === 'view',
              'p-0 d-inline-flex align-items-center justify-content-center':
                action.type !== 'view',
              'bg-light':
                action.type === 'print' && data.item.status !== 'for_download',
            },
          ]"
          :key="`action-button-${actionIndex}`"
          :title="
            data.item.status !== 'for_download' ? 'QR not ready' : action.label
          "
          @click="executeAction(action.type, data.item.uuid)"
          :disabled="
            (action.type === 'print' && data.item.status !== 'for_download') ||
            ((action.type === 'edit' || action.type === 'delete') &&
              data.item.has_occupied_table)
          "
        >
          <template v-if="!!action.icon">
            <img :src="action.icon" :alt="action.label" class="mx-auto" />
          </template>
          <template v-if="action.type === 'view'">
            {{ action.label }}
          </template>
        </b-button>
      </template>
    </b-table>
  </div>
</template>

<script>
import {
  defineComponent,
  computed,
  onMounted,
  onBeforeUnmount,
} from "@vue/composition-api";
import _ from "lodash";
import { DateTime } from "luxon";

export default defineComponent({
  setup(props, { root, emit }) {
    const loading = computed(() => root.$store.state.storeDineIn.loading);
    const tableGroup = computed(
      () => root.$store.state.storeDineIn.tableGroup || []
    );
    const apiErrors = computed(
      () => root.$store.state.storeDineIn.errors || {}
    );
    const transformedFileStatus = {
      for_download: "Ready for download",
      in_process: "In process",
    };
    const actionButtons = [
      {
        type: "edit",
        label: "Edit",
        icon: require("@/assets/images/actions/edit.svg"),
      },
      {
        type: "delete",
        label: "Delete",
        icon: require("@/assets/images/actions/delete.svg"),
      },
      {
        type: "print",
        label: "Print",
        icon: require("@/assets/images/actions/print.svg"),
      },
      {
        type: "view",
        label: "View Tables",
      },
    ];
    const tableFields = [
      {
        key: "table_name",
        thClass: "text-left",
        tdClass: "text-left",
        sortable: true,
      },
      {
        key: "branch_name",
        label: "Branch",
        sortable: true,
      },
      {
        key: "area_name",
        sortable: true,
        label: "Area in Branch",
      },
      {
        key: "branch_table_details_count",
        sortable: true,
        label: "Table Count",
      },
      {
        key: "status",
        label: "Table QR Codes",
        tdClass: (_, __, item) => {
          switch (item.status) {
            case "for_download":
              return "text-secondary";
            case "in_process":
              return "text-info";
            default:
              return "text-success";
          }
        },
        formatter: (value, _, __) => {
          return transformedFileStatus[value];
        },
        sortable: true,
      },
      {
        key: "actions",
        thClass: "text-left",
        tdClass: "text-nowrap",
      },
    ];
    const emptyText = computed(() =>
      loading.value ? "Loading..." : "No data found."
    );

    const executeAction = (type, uuid) => {
      if (type === "edit") {
        root.$router.push({
          name: "DineInUpdateTableGroup",
          params: { uuid: uuid },
        });
      } else if (type === "view") {
        root.$store.commit(
          "storeDineIn/setViewTableInfo",
          tableGroup.value.find((item) => item.uuid === uuid)
        );
        root.$router.push({ name: "ViewTables", params: { uuid: uuid } });
      } else if (type === "delete") {
        root.$emit("bv::show::modal", "dine-in-prompts", {
          title: "Delete this table group?",
          message:
            "All tables that belong to this group will be permanently deleted. Would you like to continue?",
          buttons: [
            { label: "Keep it", variant: "primary", action: "closeModal" },
            {
              label: "Yes, delete it",
              variant: "ghost btn-cancel",
              action: "handleDeleteTableGroup",
            },
          ],
          uuid: uuid,
          warning: true,
        });
      } else if (type === "print") {
        const { qr_code_url } = tableGroup.value.find(
          (table) => table.uuid === uuid
        );
        callTableQrCodeWindow(qr_code_url);
      }
    };

    const callTableQrCodeWindow = (qr_code_url) => {
      const { ts } = DateTime.now();
      window.open(
        `${qr_code_url}#${ts}`,
        "_blank",
        `width=${screen.availWidth}`,
        `height=${screen.availHeight}`
      );
    };

    const refreshList = () => {
      emit("getTableData", 1);
    };

    onMounted(() => {
      root.$on("deleteTableGroup", async (uuid) => {
        await root.$store.dispatch("storeDineIn/deleteGroupTable", {
          branch_table_uuid: uuid,
        });
        if (_.isEmpty(apiErrors.value)) {
          root.$emit("bv::hide::modal", "dine-in-prompts");
          refreshList();
        }
      });
    });

    onBeforeUnmount(() => {
      root.$off("deleteTableGroup");
    });

    return {
      loading,
      tableGroup,
      tableFields,
      emptyText,
      actionButtons,
      executeAction,
    };
  },
});
</script>

<style lang="scss" scoped>
.btn-ghost {
  width: 30px;
  height: 30px;
}
button.disabled {
  color: #fff !important;
  background-color: lightgray !important;
  border-color: transparent !important;
  opacity: 0.65;
}
</style>
